import type { Duty, Status } from "./contracts.js";

export const ANALYTICS_TIME_ZONE = "America/Los_Angeles";

export type AnalyticsGranularity = "day" | "month";
export type AnalyticsGroup = "closer" | "bdr";
export interface AnalyticsRange {
  startDate: string;
  endDate: string;
  startAt: number;
  endAt: number;
  timezone: typeof ANALYTICS_TIME_ZONE;
  granularity: AnalyticsGranularity;
}
export interface AnalyticsQuery {
  startDate: string;
  endDate: string;
  granularity: AnalyticsGranularity;
  group?: AnalyticsGroup;
  agentId?: string;
  callOffset?: number;
  intervalOffset?: number;
  activityOffset?: number;
}
export interface NormalizedAnalyticsCall {
  locationId: string;
  messageId: string;
  ghlUserId: string | null;
  contactId: string | null;
  conversationId?: string | null;
  startedAt: number;
  durationSeconds: number | null;
  direction: string;
  status: string;
  source: string;
  updatedAt: number;
}
export interface AnalyticsTotals {
  callCount: number;
  recordedDurationSeconds: number;
  unknownDurationCalls: number;
  statusMs: Record<Status, number>;
  observedMs: number;
  actionCount: number;
}
export interface AnalyticsPage<T> {
  items: T[];
  nextOffset: number | null;
}
export interface AnalyticsCallDetail extends NormalizedAnalyticsCall {
  userId: string;
}
export interface AnalyticsIntervalDetail {
  userId: string;
  status: Status;
  intent: string;
  evidenceHealth: string;
  evidenceReason: string;
  startAt: number;
  endAt: number;
}
export interface AnalyticsActionDetail {
  id: string;
  userId: string;
  type: string;
  createdAt: number;
}
export interface AnalyticsAgentReport {
  id: string;
  name: string;
  duty: Duty;
  bdrMember: boolean;
  active: boolean;
  totals: AnalyticsTotals;
}
export interface AnalyticsReport {
  range: AnalyticsRange;
  asOf: number;
  observationStartedAt: number | null;
  overall: AnalyticsTotals;
  buckets: Array<{
    key: string;
    startAt: number;
    endAt: number;
    totals: AnalyticsTotals;
  }>;
  agents: AnalyticsAgentReport[];
  availableAgents: Array<
    Pick<AnalyticsAgentReport, "id" | "name" | "duty" | "bdrMember" | "active">
  >;
  selectedAgent: null | {
    userId: string;
    calls: AnalyticsPage<AnalyticsCallDetail>;
    intervals: AnalyticsPage<AnalyticsIntervalDetail>;
    actions: AnalyticsPage<AnalyticsActionDetail>;
  };
  coverage: {
    complete: boolean;
    fresh: boolean;
    syncedAt: number | null;
    coveredThroughAt: number | null;
    unknown: boolean;
    unattributedCalls: number;
  };
}
